import { BarChartVisual } from 'src/app/pbir/visuals/highcharts/BarChartVisual.component';
import { SortDirection } from 'src/app/services/api.services';
import { VisualChartType } from '../chart-type';
import { VisualChartRoleCategorisation, PbiDataRoles } from '../data-role';
import { SortingGroups } from '../sorting-groups';

export const ColumnlikeChartTypes = {
  columnChart: <VisualChartType<BarChartVisual.Options>>{
    name: 'columnChart',
    alias: 'columnChart',
    dataRoles: {
      rows: PbiDataRoles.chartAxis, //checked
      columns: null,
      values: PbiDataRoles.chartMeasures // checked
    },
    sortingDataRoles: [PbiDataRoles.chartAxis],
    defaultSortDirection: SortDirection.Ascending,
     
    title: 'Column Chart',
    icon: 'far fa-chart-bar',
    previewImageUrl: '/assets/img/icons/chart-column-solid.svg',
    previewWhiteImageUrl: '/assets/img/icons-white/chart-column-solid.svg',
    previewRedImageUrl: '/assets/img/icons-red/chart-column-solid.svg',
    previewGreyImageUrl: '/assets/img/icons-grey/chart-column-solid.svg',

    defaultDataRoles: {
      measuresDataRole: PbiDataRoles.chartMeasures.name,
      fallbackDataRole: PbiDataRoles.chartAxis.name
    },

    getDataRoleOptions: (params) => ({
      rows: {
        maxFields: 1,
        replace: true,
      },
      values: {
        maxFields: 1,
        replace: true
      }
    }),

    roleCategorisation: VisualChartRoleCategorisation.Categorised,

    sortingGroups: [
      SortingGroups.chartAxis
    ],
 
    visualType: BarChartVisual.type,
    visualTypeOptions: {
      visualOptions: {
        ...BarChartVisual.type.visualOptions,
        direction: BarChartVisual.BarDirection.Vertical,
        displayType: BarChartVisual.DisplayType.Simple,
      },
    },
  },

  clusteredColumnChart: <VisualChartType<BarChartVisual.Options>>{
    name: 'clusteredColumnChart',
    alias: 'clusteredColumnChart',
    dataRoles: {
      rows: PbiDataRoles.chartAxis,
      columns: PbiDataRoles.chartLegend,
      values: PbiDataRoles.chartMeasures
    },
    sortingDataRoles: [PbiDataRoles.chartAxis, PbiDataRoles.chartLegend],
    defaultSortDirection: SortDirection.Ascending,
     
    title: 'Clustered Column Chart',
    icon: 'far fa-chart-bar',
    previewImageUrl: '/assets/img/icons/chart-column-clustered-solid.svg',
    previewWhiteImageUrl: '/assets/img/icons-white/chart-column-clustered-solid.svg',
    previewRedImageUrl: '/assets/img/icons-red/chart-column-clustered-solid.svg',
    previewGreyImageUrl: '/assets/img/icons-grey/chart-column-clustered-solid.svg',

    defaultDataRoles: {
      measuresDataRole: PbiDataRoles.chartMeasures.name,
      fallbackDataRole: PbiDataRoles.chartAxis.name
    },

    getDataRoleOptions: (params) => ({
      rows: {
        maxFields: 1,
        replace: true,
      },
      columns: {
        maxFields: 1,
        disabled: (params.values?.fieldCount ?? 0) > 1,
        disabledMessage: 'Cannot add items to Legend, because you have more than one item in Measures.',
        replace: true,
      },
      values: {
        ...(((params.columns?.fieldCount ?? 0) > 0) ? {
          maxFields: 1,
          replace: true
        } : {
          fieldCountForScroll: 12
        }),
      }
    }),

    roleCategorisation: VisualChartRoleCategorisation.Categorised,

    sortingGroups: [
      SortingGroups.chartAxis,
      // SortingGroups.chartLegend
    ],

    visualType: BarChartVisual.type,
    visualTypeOptions: {
      visualOptions: {
        ...BarChartVisual.type.visualOptions,
        direction: BarChartVisual.BarDirection.Vertical,
        displayType: BarChartVisual.DisplayType.Clustered,
      },
    },
  },

  stackedColumnChart: <VisualChartType<BarChartVisual.Options>>{
    name: 'stackedColumnChart',
    alias: 'stackedColumnChart',
    dataRoles: {
      rows: PbiDataRoles.chartAxis, //checked
      columns: PbiDataRoles.chartLegend, // checked
      values: PbiDataRoles.chartMeasures // checked
    },
    sortingDataRoles: [PbiDataRoles.chartAxis, PbiDataRoles.chartLegend],
    defaultSortDirection: SortDirection.Ascending,
     
    title: 'Stacked Column Chart',
    icon: 'far fa-chart-bar',
    previewImageUrl: '/assets/img/icons/chart-column-stacked-solid.svg',
    previewWhiteImageUrl: '/assets/img/icons-white/chart-column-stacked-solid.svg',
    previewRedImageUrl: '/assets/img/icons-red/chart-column-stacked-solid.svg',
    previewGreyImageUrl: '/assets/img/icons-grey/chart-column-stacked-solid.svg',

    defaultDataRoles: {
      measuresDataRole: PbiDataRoles.chartMeasures.name,
      fallbackDataRole: PbiDataRoles.chartAxis.name
    },

    getDataRoleOptions: (params) => ({
      rows: {
        maxFields: 1,
        replace: true,
      },
      columns: {
        maxFields: 1,
        disabled: (params.values?.fieldCount ?? 0) > 1,
        disabledMessage: 'Cannot add items to Legend, because you have more than one item in Measures.',
        replace: true,
      },
      values: {
        ...(((params.columns?.fieldCount ?? 0) > 0) ? {
          maxFields: 1,
          replace: true
        } : {
          fieldCountForScroll: 12
        }),
      }
    }),

    roleCategorisation: VisualChartRoleCategorisation.Categorised,

    sortingGroups: [
      SortingGroups.chartAxis,
      // SortingGroups.chartLegend
    ],

    visualType: BarChartVisual.type,
    visualTypeOptions: {
      visualOptions: {
        ...BarChartVisual.type.visualOptions,
        direction: BarChartVisual.BarDirection.Vertical,
        displayType: BarChartVisual.DisplayType.Stacked,
      },
    },
  },

  hundredPercentStackedColumnChart: <VisualChartType<BarChartVisual.Options>>{
    name: 'hundredPercentStackedColumnChart',
    alias: 'hundredPercentStackedColumnChart',
    dataRoles: {
      rows: PbiDataRoles.chartAxis, //checked
      columns: PbiDataRoles.chartLegend, //checked
      values: PbiDataRoles.chartMeasures // checked
    },
    sortingDataRoles: [PbiDataRoles.chartAxis, PbiDataRoles.chartLegend],
    defaultSortDirection: SortDirection.Ascending,
     
    title: '100% Stacked Column Chart',
    icon: 'far fa-chart-bar',
    previewImageUrl: '/assets/img/icons/chart-column-100-percent-stacked-solid.svg',
    previewWhiteImageUrl: '/assets/img/icons-white/chart-column-100-percent-stacked-solid.svg',
    previewRedImageUrl: '/assets/img/icons-red/chart-column-100-percent-stacked-solid.svg',
    previewGreyImageUrl: '/assets/img/icons-grey/chart-column-100-percent-stacked-solid.svg',

    defaultDataRoles: {
      measuresDataRole: PbiDataRoles.chartMeasures.name,
      fallbackDataRole: PbiDataRoles.chartAxis.name
    },

    getDataRoleOptions: (params) => ({
      rows: {
        maxFields: 1,
        replace: true,
      },
      columns: {
        maxFields: 1,
        disabled: (params.values?.fieldCount ?? 0) > 1,
        disabledMessage: 'Cannot add items to Legend, because you have more than one item in Measures.',
        replace: true,
      },
      values: {
        ...(((params.columns?.fieldCount ?? 0) > 0) ? {
          maxFields: 1,
          replace: true
        } : {
          fieldCountForScroll: 12
        }),
      }
    }),

    roleCategorisation: VisualChartRoleCategorisation.Categorised,

    sortingGroups: [
      SortingGroups.chartAxis,
      // SortingGroups.chartLegend
    ],

    visualType: BarChartVisual.type,
    visualTypeOptions: {
      visualOptions: {
        ...BarChartVisual.type.visualOptions,
        direction: BarChartVisual.BarDirection.Vertical,
        displayType: BarChartVisual.DisplayType.Stacked100p,
      },
    },
  },
};
